/**
 * Turns what was read from a signed lease into starting values for the review form.
 * Everything here is a suggestion; the landlord edits and confirms.
 */

export const HIGH_CONFIDENCE = 0.9;
export const CHECK_CONFIDENCE = 0.7;

const FREQUENCY_LABELS = { weekly: 'every week', biweekly: 'every two weeks', other: 'on another schedule' };

/** @returns {'clear'|'check'|'missing'|null} null when nothing was read (AI off) */
export function readingStatus(extraction, name) {
  const f = extraction?.fields?.[name];
  if (!extraction || extraction.provider === 'manual' || !extraction.fields || !Object.keys(extraction.fields).length) {
    return null;
  }
  if (!f || f.value == null || (Array.isArray(f.value) && !f.value.length)) return 'missing';
  return f.confidence >= HIGH_CONFIDENCE ? 'clear' : 'check';
}

export const STATUS_TEXT = {
  clear: 'Read from the lease',
  check: 'Please check this',
  missing: 'Not found on the lease'
};

function value(extraction, name) {
  const f = extraction?.fields?.[name];
  return f && f.value != null ? f.value : null;
}

function plainName(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function plainUnit(text) {
  return plainName(String(text || '').toLowerCase().replace(/\b(unit|suite|apt|apartment|no|number)\b\.?/g, ''));
}

export function unitDisplayName(unit) {
  return unit.name || (unit.unitNumber ? `Unit ${unit.unitNumber}` : unit.id);
}

export function tenantDisplayName(tenant) {
  if (tenant.name) return tenant.name;
  const full = `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim();
  return full || tenant.email || 'Tenant';
}

/** The property's only unit, or the one whose name matches the unit read from the lease. */
export function suggestUnitId(units, unitLabel) {
  const list = Array.isArray(units) ? units.filter((u) => u && u.id) : [];
  if (list.length === 1) return list[0].id;
  const wanted = plainUnit(unitLabel);
  if (!wanted) return '';
  const matches = list.filter((u) => [u.name, u.unitNumber, u.id].some((n) => n != null && plainUnit(n) === wanted));
  return matches.length === 1 ? matches[0].id : '';
}

/** An existing tenant whose name exactly matches one named on the lease, when exactly one does. */
export function suggestTenantId(tenants, tenantNames) {
  const names = new Set((tenantNames || []).map(plainName).filter(Boolean));
  if (!names.size) return '';
  const matches = (tenants || []).filter((t) => t.id && names.has(plainName(tenantDisplayName(t))));
  return matches.length === 1 ? matches[0].id : '';
}

/** Lease terms that have no field of their own, as plain lines for the notes. */
export function notesFromReading(extraction) {
  const lines = [];
  const names = value(extraction, 'tenantNames');
  if (Array.isArray(names) && names.length > 1) lines.push(`Tenants on the lease: ${names.join(', ')}`);
  if (value(extraction, 'leaseType') === 'periodic') lines.push('Periodic tenancy (no fixed end date)');
  const frequency = value(extraction, 'rentFrequency');
  const rent = value(extraction, 'rentAmount');
  if (frequency && frequency !== 'monthly' && rent != null) {
    lines.push(`Rent on the lease: $${rent} ${FREQUENCY_LABELS[frequency] || frequency}`);
  }
  const dueDay = value(extraction, 'rentDueDay');
  if (dueDay) lines.push(`Rent due on day ${dueDay} of the month`);
  const utilities = value(extraction, 'utilitiesIncluded');
  if (utilities) lines.push(`Utilities included: ${utilities}`);
  const parking = value(extraction, 'parkingAndStorage');
  if (parking) lines.push(`Parking and storage: ${parking}`);
  const other = value(extraction, 'otherTerms');
  if (other) lines.push(`Other terms: ${other}`);
  return lines.length ? `From the signed lease:\n${lines.join('\n')}` : '';
}

/**
 * Starting values for the review form.
 * @param {object|null} extraction - intake.extraction
 * @param {{ units?: object[] }} property - the property chosen before upload
 * @param {object[]} tenants
 */
export function initialLeaseForm(extraction, property, tenants) {
  const frequency = value(extraction, 'rentFrequency');
  const rent = value(extraction, 'rentAmount');
  const monthly = !frequency || frequency === 'monthly';
  const tenantNames = value(extraction, 'tenantNames') || [];
  const today = new Date().toISOString().slice(0, 10);
  const startDate = value(extraction, 'startDate') || '';
  return {
    unitId: suggestUnitId(property?.units, value(extraction, 'unitLabel')),
    tenantId: suggestTenantId(tenants, tenantNames),
    tenantName: tenantNames[0] || '',
    startDate,
    endDate: value(extraction, 'endDate') || '',
    monthlyRent: monthly && rent != null ? String(rent) : '',
    securityDeposit: value(extraction, 'securityDeposit') != null ? String(value(extraction, 'securityDeposit')) : '',
    lateFee: value(extraction, 'lateFee') != null ? String(value(extraction, 'lateFee')) : '',
    status: startDate && startDate > today ? 'pending' : 'active',
    notes: notesFromReading(extraction)
  };
}

/** @returns {string[]} what still needs filling in before the lease can be created */
export function leaseFormProblems(form) {
  const problems = [];
  if (!form.unitId) problems.push('Choose the unit.');
  if (!form.startDate) problems.push('Add the start date.');
  if (form.startDate && form.endDate && form.endDate <= form.startDate) problems.push('The end date must be after the start date.');
  const rent = Number(form.monthlyRent);
  if (!form.monthlyRent || !Number.isFinite(rent) || rent <= 0) problems.push('Add the monthly rent.');
  for (const [key, label] of [['securityDeposit', 'security deposit'], ['lateFee', 'late fee']]) {
    if (form[key] !== '' && (!Number.isFinite(Number(form[key])) || Number(form[key]) < 0)) {
      problems.push(`The ${label} must be a number.`);
    }
  }
  return problems;
}
